import React, { useEffect, useState, useMemo } from 'react';
import styles from './SectionTable.module.css';
import Ico from '../common/Ico';
import Btn from '../common/Btn';
import EmptyState from '../common/EmptyState';

const ICO_SEARCH    = 'M11 19a8 8 0 1 1 0-16 8 8 0 0 1 0 16zM21 21l-4.35-4.35';
const ICO_CHEVRIGHT = 'm9 18 6-6-6-6';
const ICO_CHEVLEFT  = 'm15 18-6-6 6-6';

/**
 * Shared paginated table section card.
 *
 * columns: [{ label, width, align? }]
 *   width — percentage string e.g. '12%', all columns must sum to 100%
 *   align — 'right' for right-aligned header + cell
 *
 * renderRow(item) — returns <td> elements (one per column)
 *
 * pagination="client" (default): items is the full dataset; search and paging happen
 *   entirely in this component via searchFilter/pageSize.
 * pagination="server": items is just the current page (caller already fetched it).
 *   page/totalCount/onPageChange are controlled by the caller. The search input still
 *   updates its own display value locally (for responsive typing) but debounces a call
 *   to onSearchChange instead of filtering locally — the caller owns resetting its own
 *   page state to 0 when search changes.
 */
export default function SectionTable({
  id,
  icon,
  iconBg = 'var(--blue-50)',
  iconColor = 'var(--blue-700)',
  title,
  subtitle,
  searchPlaceholder = 'Search…',
  searchFilter,
  triggerLabel,
  triggerIcon,
  onTrigger,
  items = [],
  columns = [],
  renderRow,
  getKey = item => item.id,
  onOpenItem,
  pageSize = 10,
  emptyTitle,
  emptyHint,
  pagination = 'client',
  page: serverPage,
  totalCount,
  onPageChange,
  onSearchChange,
}) {
  const isServer = pagination === 'server';
  const [query, setQuery] = useState('');
  const [page, setPage]   = useState(0);

  const filtered = useMemo(() => {
    if (isServer) return items;
    if (!query.trim() || !searchFilter) return items;
    return items.filter(item => searchFilter(item, query.trim()));
  }, [isServer, items, query, searchFilter]);

  const total       = isServer ? (totalCount ?? 0) : filtered.length;
  const currentPage = isServer ? (serverPage ?? 0) : page;
  const totalPages  = Math.ceil(total / pageSize);
  const safePage    = totalPages ? Math.min(currentPage, totalPages - 1) : 0;
  const pageItems   = isServer ? items : filtered.slice(safePage * pageSize, (safePage + 1) * pageSize);
  const from        = total ? safePage * pageSize + 1 : 0;
  const to          = isServer ? Math.min(safePage * pageSize + items.length, total) : Math.min((safePage + 1) * pageSize, total);
  const countLabel  = isServer ? total : items.length;

  useEffect(() => {
    if (!isServer && page !== safePage) setPage(safePage);
  }, [isServer, page, safePage]);

  useEffect(() => {
    if (!isServer) return;
    const t = setTimeout(() => onSearchChange?.(query), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isServer, query]);

  const goToPage = (next) => {
    if (isServer) onPageChange?.(next);
    else setPage(next);
  };

  const handleQueryChange = (e) => {
    setQuery(e.target.value);
    if (!isServer) setPage(0);
  };

  const isEmpty = isServer ? total === 0 : items.length === 0;

  return (
    <section className={styles.section} id={id}>
      <div className={styles.header}>
        <span className={styles.iconTile} style={{ background: iconBg, color: iconColor }}>
          <Ico d={icon} size={16} strokeWidth={1.7} />
        </span>
        <div className={styles.headerText}>
          <div className={styles.titleRow}>
            <span className={styles.title}>{title}</span>
            <span className={styles.countChip}>{countLabel}</span>
          </div>
          {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
        </div>
        <div className={styles.headerActions}>
          <div className={styles.searchBox}>
            <Ico d={ICO_SEARCH} size={14} style={{ color: 'var(--fg-muted)', flexShrink: 0 }} />
            <input
              className={styles.searchInput}
              value={query}
              onChange={handleQueryChange}
              placeholder={searchPlaceholder}
            />
          </div>
          {onTrigger && (
            <Btn variant="primary" size="lg" icon={triggerIcon} onClick={onTrigger}>
              {triggerLabel}
            </Btn>
          )}
        </div>
      </div>

      {isEmpty ? (
        <EmptyState icon={icon} title={emptyTitle} hint={emptyHint} />
      ) : (
        <>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <colgroup>
                {columns.map((col, i) => (
                  <col key={i} style={{ width: col.width }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  {columns.map((col, i) => (
                    <th
                      key={i}
                      className={`${styles.th}${col.align === 'right' ? ` ${styles.thRight}` : ''}`}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageItems.map(item => (
                  <tr
                    key={getKey(item)}
                    className={styles.tr}
                    onClick={() => onOpenItem?.(item)}
                  >
                    {renderRow(item)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className={styles.pagination}>
              <span className={styles.paginationText}>{from}–{to} of {total}</span>
              <div className={styles.paginationBtns}>
                <button className={styles.pageBtn} onClick={() => goToPage(safePage - 1)} disabled={safePage === 0} type="button">
                  <Ico d={ICO_CHEVLEFT} size={15} />
                </button>
                <button className={styles.pageBtn} onClick={() => goToPage(safePage + 1)} disabled={safePage + 1 >= totalPages} type="button">
                  <Ico d={ICO_CHEVRIGHT} size={15} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
